import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Save, Trash2, MessageSquareQuote, CalendarClock, Megaphone, MessageCircleMore, Pencil, CheckCircle2, RefreshCw,
  SlidersHorizontal, X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import ModalBuilder from "./ModalBuilder";
import HelpTooltip from "../shared/HelpTooltip";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { isSuperAdmin as checkIsSuperAdmin } from "../../lib/features";

const createTemplate = () => ({ id: crypto.randomUUID(), title: "", content: "" });
const createShortcut = () => ({ id: crypto.randomUUID(), label: "", day_offset: 0, hour: 9, minute: 0 });

const FIELD_TYPE_LABELS = { text: "Texto", number: "Número", date: "Data", select: "Lista (select)" };
const parseOptionsInput = (raw) => raw.split(",").map((opt) => opt.trim()).filter(Boolean);
const FIXED_PLACEHOLDERS = ["nome", "telefone", "empresa", "responsavel", "funil", "etapa"];

function SectionBlock({ icon: Icon, title, subtitle, helpText, action, children }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-[#25D366]">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h3 className="flex items-center gap-1.5 text-base font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>
              {title}
              {helpText && <HelpTooltip text={helpText} />}
            </h3>
            <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    DRAFT: "border-slate-700 bg-slate-800 text-slate-300",
    PENDING: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    APPROVED: "border-[#25D366]/30 bg-[#25D366]/10 text-[#25D366]",
    REJECTED: "border-red-500/30 bg-red-500/10 text-red-300",
    PAUSED: "border-purple-500/30 bg-purple-500/10 text-purple-300",
    DISABLED: "border-slate-600 bg-slate-800 text-slate-300",
  };
  const labels = {
    DRAFT: "Rascunho",
    PENDING: "Em análise",
    APPROVED: "Aprovado",
    REJECTED: "Rejeitado",
    PAUSED: "Pausado",
    DISABLED: "Desativado",
  };
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${styles[status] || styles.DRAFT}`}>
      {labels[status] || "Rascunho"}
    </span>
  );
}

export default function ProductivitySettings({
  data,
  metaWhatsApp,
  onSave,
  onSaveOfficialTemplate,
  onSubmitOfficialTemplate,
  onDeleteOfficialTemplate,
  onSyncOfficialTemplates,
  busy,
}) {
  const { user } = useAuth();
  const isSuperAdmin = checkIsSuperAdmin(user);
  const activeMetaInstance = (metaWhatsApp?.instances || []).find((item) => item?.enabled) || metaWhatsApp || {};
  const [form, setForm] = useState({
    custom_templates: [],
    schedule_shortcuts: [],
    official_whatsapp_templates: [],
  });
  const [builderOpen, setBuilderOpen] = useState(false);
  const [activeOfficialTemplate, setActiveOfficialTemplate] = useState(null);
  const [taskTypes, setTaskTypes] = useState([]);
  const [newTaskTypeName, setNewTaskTypeName] = useState("");
  const [editingTaskTypeId, setEditingTaskTypeId] = useState(null);
  const [editingTaskTypeName, setEditingTaskTypeName] = useState("");
  const canManageTaskTypes = ["owner", "admin", "super_admin"].includes(user?.role);

  const [customFields, setCustomFields] = useState([]);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState("text");
  const [newFieldOptions, setNewFieldOptions] = useState("");
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editingFieldLabel, setEditingFieldLabel] = useState("");
  const [editingFieldType, setEditingFieldType] = useState("text");
  const [editingFieldOptions, setEditingFieldOptions] = useState("");
  const canManageCustomFields = ["owner", "admin", "super_admin"].includes(user?.role);

  useEffect(() => {
    setForm({
      custom_templates: data?.custom_templates?.filter((item) => !String(item?.id || "").startsWith("waba-"))?.length
        ? data.custom_templates.filter((item) => !String(item?.id || "").startsWith("waba-"))
        : [createTemplate(), createTemplate()],
      schedule_shortcuts: data?.schedule_shortcuts?.length ? data.schedule_shortcuts : [createShortcut()],
      official_whatsapp_templates: data?.official_whatsapp_templates?.length ? data.official_whatsapp_templates : [],
    });
  }, [data]);

  const loadTaskTypes = async () => {
    try {
      const { data } = await api.get("/task-types?include_inactive=true");
      setTaskTypes(Array.isArray(data) ? data : []);
    } catch {
      setTaskTypes([]);
    }
  };

  useEffect(() => {
    loadTaskTypes();
    loadCustomFields();
  }, []);

  const loadCustomFields = async () => {
    try {
      const { data } = await api.get("/custom-field-definitions?include_inactive=true");
      setCustomFields(Array.isArray(data) ? data : []);
    } catch {
      setCustomFields([]);
    }
  };

  const createCustomField = async () => {
    const label = newFieldLabel.trim();
    if (!label) return;
    const options = newFieldType === "select" ? parseOptionsInput(newFieldOptions) : [];
    if (newFieldType === "select" && options.length === 0) {
      toast.error("Adicione ao menos uma opção (separadas por vírgula)");
      return;
    }
    try {
      await api.post("/custom-field-definitions", { label, type: newFieldType, options });
      setNewFieldLabel("");
      setNewFieldType("text");
      setNewFieldOptions("");
      await loadCustomFields();
      toast.success("Campo personalizado criado");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao criar campo personalizado");
    }
  };

  const startEditCustomField = (item) => {
    setEditingFieldId(item.id);
    setEditingFieldLabel(item.label || "");
    setEditingFieldType(item.type || "text");
    setEditingFieldOptions((item.options || []).join(", "));
  };

  const saveCustomField = async (id) => {
    const label = editingFieldLabel.trim();
    if (!label) return;
    const options = editingFieldType === "select" ? parseOptionsInput(editingFieldOptions) : [];
    if (editingFieldType === "select" && options.length === 0) {
      toast.error("Adicione ao menos uma opção (separadas por vírgula)");
      return;
    }
    try {
      await api.patch(`/custom-field-definitions/${id}`, { label, type: editingFieldType, options });
      setEditingFieldId(null);
      await loadCustomFields();
      toast.success("Campo personalizado atualizado");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao editar campo personalizado");
    }
  };

  const removeCustomField = async (item) => {
    if (!window.confirm(`Remover o campo "${item.label}"? Valores já preenchidos em leads não serão apagados.`)) return;
    try {
      await api.delete(`/custom-field-definitions/${item.id}`);
      await loadCustomFields();
      toast.success("Campo personalizado removido");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao remover campo personalizado");
    }
  };

  const sortedOfficialTemplates = useMemo(
    () => [...(form.official_whatsapp_templates || [])].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)),
    [form.official_whatsapp_templates]
  );

  const updateListItem = (key, id, patch) => {
    setForm((current) => ({
      ...current,
      [key]: current[key].map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  };

  const removeListItem = (key, id) => {
    setForm((current) => ({
      ...current,
      [key]: current[key].filter((item) => item.id !== id),
    }));
  };

  const addListItem = (key, factory) => {
    setForm((current) => ({
      ...current,
      [key]: [...current[key], factory()],
    }));
  };

  const submit = () => {
    onSave({
      custom_templates: form.custom_templates,
      schedule_shortcuts: form.schedule_shortcuts,
      official_whatsapp_templates: form.official_whatsapp_templates,
    });
  };

  const openNewOfficialTemplate = () => {
    setActiveOfficialTemplate({
      id: crypto.randomUUID(),
      name: "",
      category: "MARKETING",
      language: "pt_BR",
      header_type: "NONE",
      header_text: "",
      body: "",
      footer: "",
      buttons: [],
      status: "DRAFT",
    });
    setBuilderOpen(true);
  };

  const createTaskType = async () => {
    const name = newTaskTypeName.trim();
    if (!name) return;
    try {
      await api.post("/task-types", { name });
      setNewTaskTypeName("");
      await loadTaskTypes();
      toast.success("Opção de tarefa adicionada");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao adicionar opção");
    }
  };

  const saveTaskType = async (id) => {
    const name = editingTaskTypeName.trim();
    if (!name) return;
    try {
      await api.patch(`/task-types/${id}`, { name });
      setEditingTaskTypeId(null);
      setEditingTaskTypeName("");
      await loadTaskTypes();
      toast.success("Opção de tarefa atualizada");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao editar opção");
    }
  };

  const toggleTaskType = async (item, active) => {
    try {
      await api.patch(`/task-types/${item.id}`, { active });
      await loadTaskTypes();
      toast.success(active ? "Opção reativada" : "Opção desativada");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao atualizar opção");
    }
  };

  return (
    <div className="space-y-5">
      <SectionBlock
        icon={MessageSquareQuote}
        title="Templates de 1 Clique"
        subtitle="Edite as frases rápidas usadas no painel do lead."
        helpText="Use {placeholders} entre chaves pra puxar dados do lead automaticamente (ex: {nome}, {telefone}). Veja a lista completa de placeholders disponíveis logo abaixo — inclui os campos personalizados que você criar."
      >
        <div className="mb-3 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-[11px] text-slate-400">
          Placeholders disponíveis:{" "}
          {[...FIXED_PLACEHOLDERS, ...customFields.filter((f) => f.active !== false).map((f) => f.key)]
            .map((key) => `{${key}}`)
            .join(", ")}
          {customFields.length === 0 && " — crie campos personalizados abaixo para adicionar mais."}
        </div>
        <div className="space-y-3">
          {form.custom_templates.map((template) => (
            <div key={template.id} className="grid grid-cols-1 items-end gap-3 md:grid-cols-[160px_1fr_auto]">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Título</Label>
                <Input value={template.title} onChange={(e) => updateListItem("custom_templates", template.id, { title: e.target.value })} className="mt-1 border-slate-700 bg-slate-800 text-slate-100" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Conteúdo</Label>
                <Input value={template.content} onChange={(e) => updateListItem("custom_templates", template.id, { content: e.target.value })} className="mt-1 border-slate-700 bg-slate-800 text-slate-100" />
              </div>
              <Button type="button" variant="ghost" onClick={() => removeListItem("custom_templates", template.id)} className="text-slate-400 hover:bg-slate-800 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => addListItem("custom_templates", createTemplate)} className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800">
            <Plus className="mr-1.5 h-4 w-4" /> Novo template
          </Button>
        </div>
      </SectionBlock>

      <SectionBlock
        icon={SlidersHorizontal}
        title="Campos Personalizados"
        subtitle="Crie campos extras para os leads. Ficam disponíveis no card do lead e como placeholder {chave} em templates e automações."
        helpText="Campos que o seu negócio precisa e o CRM não tem de fábrica (ex: Produto, Tamanho, Data de Nascimento). Depois de criados, aparecem no modal Editar Lead e podem ser usados como filtro/relatório no Analytics."
      >
        <div className="space-y-3">
          {canManageCustomFields && (
            <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-[1fr_140px_1fr_auto]">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Nome do campo</Label>
                <Input
                  value={newFieldLabel}
                  onChange={(e) => setNewFieldLabel(e.target.value)}
                  placeholder="Ex: Data de Nascimento"
                  className="mt-1 border-slate-700 bg-slate-800 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Tipo</Label>
                <select
                  value={newFieldType}
                  onChange={(e) => setNewFieldType(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#25D366]/50"
                >
                  {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                {newFieldType === "select" ? (
                  <>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-400">Opções (separadas por vírgula)</Label>
                    <Input
                      value={newFieldOptions}
                      onChange={(e) => setNewFieldOptions(e.target.value)}
                      placeholder="Ex: P, M, G"
                      className="mt-1 border-slate-700 bg-slate-800 text-slate-100"
                    />
                  </>
                ) : (
                  <div className="text-[11px] text-slate-600 mt-6">— sem opções para este tipo —</div>
                )}
              </div>
              <Button type="button" onClick={createCustomField} className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]">
                <Plus className="mr-1.5 h-4 w-4" /> Adicionar
              </Button>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-slate-800">
            {customFields.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">Nenhum campo personalizado cadastrado.</div>
            ) : customFields.map((item) => (
              <div key={item.id} className={`border-b border-slate-800 px-4 py-3 last:border-0 ${item.active === false ? "opacity-50" : ""}`}>
                {editingFieldId === item.id ? (
                  <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-[1fr_140px_1fr_auto_auto]">
                    <Input value={editingFieldLabel} onChange={(e) => setEditingFieldLabel(e.target.value)} className="border-slate-700 bg-slate-800 text-slate-100" />
                    <select
                      value={editingFieldType}
                      onChange={(e) => setEditingFieldType(e.target.value)}
                      className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#25D366]/50"
                    >
                      {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    {editingFieldType === "select" ? (
                      <Input
                        value={editingFieldOptions}
                        onChange={(e) => setEditingFieldOptions(e.target.value)}
                        placeholder="Ex: P, M, G"
                        className="border-slate-700 bg-slate-800 text-slate-100"
                      />
                    ) : <div />}
                    <Button type="button" size="sm" onClick={() => saveCustomField(item.id)} className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]">
                      Salvar
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => setEditingFieldId(null)} className="text-slate-400 hover:bg-slate-800 hover:text-slate-50">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-slate-100">{item.label}</p>
                        <span className="rounded-full border border-slate-700 bg-slate-800 px-2 py-0.5 text-[10px] text-slate-400">{FIELD_TYPE_LABELS[item.type] || item.type}</span>
                        {item.active === false && <span className="text-[10px] text-slate-500">(inativo)</span>}
                      </div>
                      <p className="mt-1 text-[11px] text-slate-500">
                        Placeholder: <code className="text-slate-400">{`{${item.key}}`}</code>
                        {item.type === "select" && item.options?.length ? ` · Opções: ${item.options.join(", ")}` : ""}
                      </p>
                    </div>
                    {canManageCustomFields && (
                      <>
                        <Button type="button" variant="ghost" size="icon" onClick={() => startEditCustomField(item)} className="text-slate-400 hover:bg-slate-800 hover:text-slate-50">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeCustomField(item)} className="text-slate-400 hover:bg-slate-800 hover:text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </SectionBlock>

      <SectionBlock
        icon={CalendarClock}
        title="Atalhos de Agendamento"
        subtitle="Defina os rótulos e horários padrão do menu Agendar."
        helpText="Estes atalhos aparecem no botão 'Agendar' do chat do lead: cada um monta automaticamente uma mensagem propondo reunião no dia/hora escolhido (ex: 'Amanhã 10:00'), pronta pra enviar. Dias é o deslocamento a partir de hoje (0 = hoje, 1 = amanhã); Hora/Min é o horário proposto nesse dia."
      >
        <div className="space-y-3">
          {form.schedule_shortcuts.map((shortcut) => (
            <div key={shortcut.id} className="grid grid-cols-1 items-end gap-3 md:grid-cols-[1fr_100px_90px_90px_auto]">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Rótulo</Label>
                <Input value={shortcut.label} onChange={(e) => updateListItem("schedule_shortcuts", shortcut.id, { label: e.target.value })} className="mt-1 border-slate-700 bg-slate-800 text-slate-100" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Dias</Label>
                <Input type="number" value={shortcut.day_offset} onChange={(e) => updateListItem("schedule_shortcuts", shortcut.id, { day_offset: Number(e.target.value) || 0 })} className="mt-1 border-slate-700 bg-slate-800 text-slate-100" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Hora</Label>
                <Input type="number" value={shortcut.hour} onChange={(e) => updateListItem("schedule_shortcuts", shortcut.id, { hour: Number(e.target.value) || 0 })} className="mt-1 border-slate-700 bg-slate-800 text-slate-100" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Min</Label>
                <Input type="number" value={shortcut.minute} onChange={(e) => updateListItem("schedule_shortcuts", shortcut.id, { minute: Number(e.target.value) || 0 })} className="mt-1 border-slate-700 bg-slate-800 text-slate-100" />
              </div>
              <Button type="button" variant="ghost" onClick={() => removeListItem("schedule_shortcuts", shortcut.id)} className="text-slate-400 hover:bg-slate-800 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => addListItem("schedule_shortcuts", createShortcut)} className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800">
            <Plus className="mr-1.5 h-4 w-4" /> Novo atalho
          </Button>
        </div>
      </SectionBlock>

      <SectionBlock
        icon={CheckCircle2}
        title="Opções de Tarefas"
        subtitle="Tipos disponíveis no cadastro de tarefas dentro do lead."
      >
        <div className="space-y-3">
          {canManageTaskTypes && (
            <div className="flex gap-2">
              <Input
                value={newTaskTypeName}
                onChange={(e) => setNewTaskTypeName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createTaskType()}
                placeholder="Nova opção"
                className="border-slate-700 bg-slate-800 text-slate-100"
              />
              <Button type="button" onClick={createTaskType} className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]">
                <Plus className="mr-1.5 h-4 w-4" /> Adicionar
              </Button>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-slate-800">
            {taskTypes.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">Nenhuma opção cadastrada.</div>
            ) : taskTypes.map((item) => (
              <div key={item.id} className="flex items-center gap-3 border-b border-slate-800 px-4 py-3 last:border-0">
                <div className="flex-1 min-w-0">
                  {editingTaskTypeId === item.id ? (
                    <Input
                      value={editingTaskTypeName}
                      onChange={(e) => setEditingTaskTypeName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveTaskType(item.id)}
                      className="border-slate-700 bg-slate-800 text-slate-100"
                    />
                  ) : (
                    <>
                      <p className="truncate text-sm font-semibold text-slate-100">{item.name}</p>
                      <p className="text-[11px] text-slate-500">{item.active ? "Ativa" : "Inativa"}</p>
                    </>
                  )}
                </div>
                {canManageTaskTypes && editingTaskTypeId === item.id ? (
                  <Button type="button" size="sm" onClick={() => saveTaskType(item.id)} className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]">
                    Salvar
                  </Button>
                ) : canManageTaskTypes ? (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingTaskTypeId(item.id);
                        setEditingTaskTypeName(item.name || "");
                      }}
                      className="text-slate-400 hover:bg-slate-800 hover:text-slate-50"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleTaskType(item, !item.active)}
                      className={item.active ? "text-slate-400 hover:bg-slate-800 hover:text-red-400" : "text-slate-400 hover:bg-slate-800 hover:text-[#25D366]"}
                    >
                      {item.active ? <Trash2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </SectionBlock>

      {isSuperAdmin && (
      <SectionBlock
        icon={Megaphone}
        title="Gerenciador de Templates Oficiais do WhatsApp"
        subtitle="Crie, visualize e envie modelos oficiais para aprovação da Meta."
        action={(
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={onSyncOfficialTemplates} disabled={busy} className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800">
              <RefreshCw className="mr-1.5 h-4 w-4" /> Sincronizar status
            </Button>
            <Button type="button" onClick={openNewOfficialTemplate} className="bg-[#25D366] text-slate-950 hover:bg-[#1EAC52]">
              <Plus className="mr-1.5 h-4 w-4" /> Novo modelo
            </Button>
          </div>
        )}
      >
        <div className="overflow-hidden rounded-2xl border border-slate-800">
          <div className="grid grid-cols-[1.5fr_120px_120px_140px_1fr_160px] gap-3 border-b border-slate-800 bg-slate-950/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <span>Nome</span>
            <span>Canal</span>
            <span>Status</span>
            <span>Categoria</span>
            <span>Idioma</span>
            <span>Ações</span>
          </div>
          <div className="divide-y divide-slate-800 bg-slate-900/30">
            {sortedOfficialTemplates.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                Nenhum template oficial cadastrado ainda.
              </div>
            ) : (
              sortedOfficialTemplates.map((template) => (
                <div key={template.id} className="grid grid-cols-[1.5fr_120px_120px_140px_1fr_160px] items-center gap-3 px-4 py-3 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-slate-100">{template.name || "Template sem nome"}</div>
                    <div className="truncate text-xs text-slate-500">{template.body || "Sem corpo definido"}</div>
                  </div>
                  <div className="text-slate-300">WhatsApp</div>
                  <div><StatusBadge status={template.status} /></div>
                  <div className="text-slate-300">{template.category === "UTILITY" ? "Utilidade" : "Marketing"}</div>
                  <div className="text-slate-300">{template.language === "pt_BR" ? "Português (BR)" : template.language}</div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setActiveOfficialTemplate(template);
                        setBuilderOpen(true);
                      }}
                      className="text-slate-400 hover:bg-slate-800 hover:text-slate-50"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeleteOfficialTemplate?.(template.id)}
                      className="text-slate-400 hover:bg-slate-800 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy || !template.name?.trim() || !template.category || !template.body?.trim()}
                      onClick={() => onSubmitOfficialTemplate?.(template)}
                      className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
                    >
                      <MessageCircleMore className="mr-1.5 h-4 w-4" /> Enviar
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-xs text-slate-400">
          Conta conectada: <span className="font-semibold text-slate-200">{activeMetaInstance?.waba_id || "WABA ID não configurado"}</span>
        </div>
      </SectionBlock>
      )}

      <div className="sticky bottom-0 z-10 flex justify-end border-t border-slate-800 bg-slate-950/95 px-1 pb-1 pt-4 backdrop-blur">
        <Button onClick={submit} disabled={busy} className="bg-[#25D366] font-semibold text-slate-950 hover:bg-[#1EAC52]">
          <Save className="mr-1.5 h-4 w-4" /> Salvar Alterações
        </Button>
      </div>

      <ModalBuilder
        open={builderOpen}
        template={activeOfficialTemplate}
        busy={busy}
        onClose={() => {
          setBuilderOpen(false);
          setActiveOfficialTemplate(null);
        }}
        onSaveDraft={onSaveOfficialTemplate}
        onSubmitTemplate={onSubmitOfficialTemplate}
      />
    </div>
  );
}
